"use client";

import { useAuiState } from "@assistant-ui/react";
import {
    type CodeHeaderProps,
    MarkdownTextPrimitive,
    unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
    useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import * as Sentry from "@sentry/react";
import type { Element as HastElement } from "hast";
import { CheckIcon, CopyIcon, ExternalLink, FileText } from "lucide-react";
import {
    Component,
    type ComponentPropsWithoutRef,
    type FC,
    type ReactElement,
    type ReactNode,
    isValidElement,
    memo,
    useMemo,
    useState,
} from "react";
import { defaultUrlTransform } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import { ChatTable } from "@/components/assistant-ui/chat-table";
import { InlineSourceChip } from "@/components/assistant-ui/chat-table-cells";
import { EditorialGlyph } from "@/components/assistant-ui/editorial-glyph";
import {
    type EditorialComponentPolicy,
    EditorialReactInline,
} from "@/components/assistant-ui/editorial-react-inline";
import { areSpanNodesEqual } from "@/components/assistant-ui/editorial-span-memo";
import {
    CUSTOM_HREF_ATTR,
    parseLink,
    useMarkdownActions,
} from "@/components/assistant-ui/markdown-actions";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import {
    editorialRehypePlugins,
    plainRehypePlugins,
} from "@/components/assistant-ui/utils/editorial-pipeline";
import { getEditorialProvenance } from "@/components/assistant-ui/utils/editorial-provenance";
import { parseMarkdownTable } from "@/components/assistant-ui/utils/parse-markdown-table";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// react-markdown's default urlTransform strips non-standard protocols.
// Allow button://, link://, and cite:// through for CustomLink handling, but
// preserve default sanitization (blocks javascript:, data:, etc.).
function urlTransform(url: string): string {
    if (/^(button|link|cite):\/\//.test(url)) return url;
    return defaultUrlTransform(url);
}

// rehype-raw promotes raw HTML, so we close the parallel hole that
// `urlTransform` doesn't cover: elements without an href/src that are
// dangerous by their mere presence.
const DISALLOWED_RAW_HTML_ELEMENTS = [
    "script",
    "iframe",
    "style",
    "object",
    "embed",
    "link",
    "meta",
    "form",
    "base",
] as const;

/** Which typesetting policy a mount opts into. Absent means the plain path. */
export type EditorialTypesettingPolicy = "none" | "polly-editorial-v1";

// Hoisted to module scope, one list per policy. Built inline these would be a
// fresh array on every render, and during streaming every token is a render, so
// this saves an allocation per token.
//
// That allocation is the whole of the claim. It is NOT a saved pipeline
// rebuild: neither `@assistant-ui/react-markdown` nor `react-markdown` memoises
// on plugin identity — react-markdown calls `unified()` to construct its
// processor on every render whatever array it is handed. Measured, and recorded
// with the evidence in `utils/editorial-pipeline.ts`.
//
// Order is load-bearing. The structural pass runs immediately after
// `rehypeRaw`, so it scrubs reserved namespaces before anything reads them and
// records heading adjacency while it still exists; the title lift then removes
// headings it consumes; typesetting runs after that, so no source-model
// consumer ever reads decorated HAST.
//
// The tail anchor is LAST and is in BOTH lists. It is the streaming cursor, and
// a cursor that moved with the policy would strand every plain render —
// including the fail-open fallback of a message that is still streaming, which
// is exactly when the cursor matters. Only the editorial PAIR is policy-gated.
const REMARK_PLUGINS = [remarkGfm];

const PLAIN_REHYPE_PLUGINS = [rehypeRaw, ...plainRehypePlugins()];

// Block treatments run AFTER the inline transform and BEFORE the tail anchor.
// After, because a warning-led paragraph is recognized by the provenance-backed
// glyph the inline pass already minted — reading a trusted mark rather than
// re-deriving the leading token, which would be the second rendered-order
// traversal the inline-run cursor exists to prevent. Before the anchor, because
// the anchor must see the final display tree.
const EDITORIAL_REHYPE_PLUGINS = [rehypeRaw, ...editorialRehypePlugins()];

/**
 * The policy is chosen by WHICH component fills the `Text` slot, not by a prop.
 *
 * `MessagePrimitive.Parts` types its slots as components taking the text-part
 * props, so a component declaring any prop of its own stops being assignable —
 * measured, on all four existing call sites. Two components keep every current
 * mount working untouched and make the opt-in explicit at the slot.
 */
const renderMarkdown = (
    rehypePlugins:
        | typeof PLAIN_REHYPE_PLUGINS
        | typeof EDITORIAL_REHYPE_PLUGINS,
    components: typeof defaultComponents
) => (
    <MarkdownTextPrimitive
        remarkPlugins={REMARK_PLUGINS}
        // rehype-raw promotes raw HTML into hast nodes so the `span`
        // override can read `data-status` / `data-bool` from Polly's
        // tagging contract. The `disallowedElements` allowlist plus
        // urlTransform together keep the attack surface narrow.
        rehypePlugins={rehypePlugins}
        disallowedElements={[...DISALLOWED_RAW_HTML_ELEMENTS]}
        className="aui-md"
        components={components}
        urlTransform={urlTransform}
    />
);

/**
 * Only the frames. A thrown error's `message` can quote the source that
 * produced it, and the first line of `stack` repeats that message — so a
 * report that forwards either can carry message content out of the app. The
 * frames identify the failing code without quoting anything the user wrote.
 */
const stackFramesOnly = (stack: string | undefined): string =>
    (stack ?? "")
        .split("\n")
        .filter((line) => /^\s*at\s/.test(line))
        .join("\n");

/**
 * Per-message fail-open. One message's typesetting fault must never blank the
 * thread: this catches it, reports it without content, and re-renders that
 * message on the plain path — where the tail anchor still runs, so a message
 * that failed WHILE STREAMING keeps its cursor.
 *
 * Reports explicitly rather than wrapping Sentry's own `ErrorBoundary`,
 * because what is sent has to be controlled here: the contract is that message
 * content never reaches the report, and that is a property of the payload we
 * construct, not of a component we hand an error to.
 *
 * The fallback is permanent for the affected message. A boundary that reset on
 * new content would re-enter the failing path on every streaming chunk and
 * report once per chunk.
 */
export class EditorialFailOpenBoundary extends Component<
    { readonly fallback: ReactNode; readonly children: ReactNode },
    { readonly failed: boolean }
> {
    override state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    override componentDidCatch(error: Error) {
        const sanitized = new Error(
            "Editorial typesetting failed; message rendered on the plain path"
        );
        sanitized.name = "EditorialTypesettingError";
        sanitized.stack = stackFramesOnly(error?.stack);

        Sentry.captureException(sanitized, {
            tags: { feature: "editorial-typesetting" },
        });
    }

    override render() {
        return this.state.failed ? this.props.fallback : this.props.children;
    }
}

const MarkdownTextImpl = () =>
    renderMarkdown(PLAIN_REHYPE_PLUGINS, defaultComponents);

const EditorialMarkdownTextImpl = () => (
    <EditorialFailOpenBoundary
        fallback={renderMarkdown(PLAIN_REHYPE_PLUGINS, defaultComponents)}
    >
        {renderMarkdown(EDITORIAL_REHYPE_PLUGINS, editorialComponents)}
    </EditorialFailOpenBoundary>
);

/** The plain path. Unchanged output, plus the inert streaming-tail anchor. */
export const MarkdownText = memo(MarkdownTextImpl);

/** The same renderer under `polly-editorial-v1`. */
export const EditorialMarkdownText = memo(EditorialMarkdownTextImpl);

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
    const { isCopied, copyToClipboard } = useCopyToClipboard();
    const onCopy = () => {
        if (!code || isCopied) return;
        copyToClipboard(code);
    };

    return (
        <div className="aui-code-header-root border-border/50 bg-muted/50 mt-2.5 flex items-center justify-between rounded-t-lg border border-b-0 px-3 py-1.5 text-xs">
            <span className="aui-code-header-language text-muted-foreground font-medium lowercase">
                {language}
            </span>
            <TooltipIconButton tooltip="Copy" onClick={onCopy}>
                {!isCopied && <CopyIcon />}
                {isCopied && <CheckIcon />}
            </TooltipIconButton>
        </div>
    );
};

const useCopyToClipboard = ({
    copiedDuration = 3000,
}: {
    copiedDuration?: number;
} = {}) => {
    const [isCopied, setIsCopied] = useState<boolean>(false);

    const copyToClipboard = (value: string) => {
        if (!value) return;

        navigator.clipboard.writeText(value).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), copiedDuration);
        });
    };

    return { isCopied, copyToClipboard };
};

/** Plain HTML fallback for streaming and unparseable tables. */
export function BasicTable({
    className,
    ...props
}: ComponentPropsWithoutRef<"table">) {
    return (
        <div className="aui-md-table-wrapper my-2 ml-[calc(-50cqw+50%)] w-[100cqw] px-4">
            <div className="mx-auto w-max max-w-full overflow-x-auto">
                <table
                    className={cn(
                        "aui-md-table bg-background w-max border-separate border-spacing-0 overflow-hidden rounded-lg shadow-sm",
                        className
                    )}
                    {...props}
                />
            </div>
        </div>
    );
}

interface MarkdownChatTableProps extends ComponentPropsWithoutRef<"table"> {
    /** react-markdown attaches the hast `<table>` element here; always
     * present at runtime, optional only for the structural prop type. */
    node?: HastElement;
}

// GFM `<table>` override. Routes completed, parseable assistant tables
// to interactive `<ChatTable>`; streaming / unparseable / no-node falls
// through to `<BasicTable>`. Module-scope identity matters: a fresh
// override per parent render would remount the table and reset
// TanStack's sort/filter state on every streaming token.
export function MarkdownChatTable({
    node,
    children,
    ...props
}: MarkdownChatTableProps) {
    const isStreaming = useAuiState(
        (s) => s.message.status?.type === "running"
    );
    // Streaming guard lives inside the memo because `node` reference
    // changes per token; a guard outside the memo would bust the cache
    // and run `parseMarkdownTable` (full hast walk) on every token only
    // to throw the result away.
    const finalData = useMemo(() => {
        if (isStreaming || !node) return null;
        const parsed = parseMarkdownTable(node);
        if (!parsed) return null;
        const inferredTitle = node.properties?.dataInferredTitle;
        return typeof inferredTitle === "string" && inferredTitle.length > 0
            ? { ...parsed, title: inferredTitle }
            : parsed;
    }, [node, isStreaming]);

    if (!finalData) {
        return <BasicTable {...props}>{children}</BasicTable>;
    }
    return <ChatTable data={finalData} />;
}

// react-markdown reuses hast nodes when content is unchanged, so
// reference identity covers the common case. When refs differ we let
// React re-render; the inner `useMemo` short-circuits during
// streaming and skips `parseMarkdownTable` so the cost is small.
export interface EditorialSpanProps extends ComponentPropsWithoutRef<"span"> {
    readonly node?: HastElement;
}

function areTableNodesEqual(
    prev: MarkdownChatTableProps,
    next: MarkdownChatTableProps
): boolean {
    if (!prev.node || !next.node) return false;
    if (prev.node === next.node) return true;
    return (
        prev.node.children === next.node.children &&
        prev.node.properties === next.node.properties
    );
}

/**
 * Exported so a test can assert the fence is INSTALLED, not merely that its
 * comparator is correct. A comparator can be perfect and unreferenced.
 */
export const MemoizedMarkdownChatTable = memo(
    MarkdownChatTable,
    areTableNodesEqual
);

/**
 * Two jobs. A span this layer MINTED becomes an `EditorialGlyph`; every other
 * span is an identity passthrough, so `data-status` / `data-bool` survive
 * react-markdown's prop pruning into the rendered DOM.
 *
 * Routing is gated on PROVENANCE, never on the `data-editorial-glyph` attribute
 * beside it. That attribute is authorable from raw markdown and proves nothing;
 * the provenance symbol cannot be written from source and does not survive a
 * serialize/reparse, so forged markup falls through to the passthrough branch
 * and renders as the ordinary span it is.
 */
export function EditorialSpan({ node, ...props }: EditorialSpanProps) {
    const provenance = getEditorialProvenance(node);
    if (provenance?.kind !== "glyph" || provenance.semantic === undefined) {
        return <span {...props} />;
    }

    // Usage rides on the provenance, not on a `data-` attribute: an authorable
    // usage would let a message pick its own accessible label. The terminal
    // transform mints `inline`; a block treatment re-marks the glyph it adopts,
    // so the absent case is the inline one. The manifest stays the authority on
    // whether the pair may be presented at all — `EditorialGlyph` checks
    // (semantic, usage) against it and renders the authored source if not.
    return (
        <EditorialGlyph
            semantic={provenance.semantic}
            usage={provenance.usage ?? "inline"}
        >
            {props.children}
        </EditorialGlyph>
    );
}

/** Exported for the same installation assertion as the table fence above. */
export const MemoizedEditorialSpan = memo(EditorialSpan, areSpanNodesEqual);

// Hoisted so the editorial cell variants below are the SAME cell with a
// different child treatment, not a second copy of the styling.
const TH_CLASS =
    "aui-md-th px-3 py-2 text-left text-sm font-semibold [[align=center]]:text-center [[align=right]]:text-right";

const TD_CLASS =
    "aui-md-td border-border max-w-[400px] min-w-[60px] border-b px-3 py-2 text-left align-top text-sm [[align=center]]:text-center [[align=right]]:text-right last:[tbody_tr:last-child_&]:border-b-0";

/**
 * Every string a label contributes, with its elements left behind. An image
 * contributes its `alt`; no other prop stands in for text.
 */
const labelTextOf = (node: ReactNode): string => {
    if (typeof node === "string" || typeof node === "number") {
        return String(node);
    }
    if (Array.isArray(node)) return node.map(labelTextOf).join("");
    if (!isValidElement(node)) return "";
    const element = node as ReactElement<{
        alt?: unknown;
        children?: ReactNode;
    }>;
    if (element.type === "img" && typeof element.props.alt === "string") {
        return element.props.alt;
    }
    return labelTextOf(element.props.children);
};

const memoizedDefaultComponents = memoizeMarkdownComponents({
    // Sizes and weights are deliberately absent: they come from the global
    // heading defaults in style.css.
    h1: ({ className, ...props }) => (
        <h1
            className={cn(
                "aui-md-h1 text-primary mt-4 mb-2 scroll-m-20 first:mt-0 last:mb-0",
                className
            )}
            {...props}
        />
    ),
    h2: ({ className, ...props }) => (
        <h2
            className={cn(
                "aui-md-h2 text-primary mt-3 mb-1.5 scroll-m-20 first:mt-0 last:mb-0",
                className
            )}
            {...props}
        />
    ),
    h3: ({ className, ...props }) => (
        <h3
            className={cn(
                "aui-md-h3 text-primary mt-2.5 mb-1 scroll-m-20 first:mt-0 last:mb-0",
                className
            )}
            {...props}
        />
    ),
    h4: ({ className, ...props }) => (
        <h4
            className={cn(
                "aui-md-h4 text-primary mt-2 mb-1 scroll-m-20 first:mt-0 last:mb-0",
                className
            )}
            {...props}
        />
    ),
    h5: ({ className, ...props }) => (
        <h5
            className={cn(
                "aui-md-h5 text-primary mt-2 mb-1 first:mt-0 last:mb-0",
                className
            )}
            {...props}
        />
    ),
    h6: ({ className, ...props }) => (
        <h6
            className={cn(
                "aui-md-h6 text-primary mt-2 mb-1 first:mt-0 last:mb-0",
                className
            )}
            {...props}
        />
    ),
    p: ({ className, ...props }) => (
        <p
            className={cn(
                "aui-md-p my-2.5 leading-normal first:mt-0 last:mb-0",
                className
            )}
            {...props}
        />
    ),
    a: function CustomLink({ href, className, children, ...props }) {
        const { onNavigate, onSource } = useMarkdownActions();

        if (!href) return <>{children}</>;

        const parsed = parseLink(href);
        if (!parsed) {
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                        "aui-md-a text-primary hover:text-primary/80 underline underline-offset-2",
                        className
                    )}
                    {...props}
                >
                    {children}
                </a>
            );
        }

        const { protocol, action, target, params } = parsed;

        // Native citation handle: render the resolving chip; it owns its own
        // click (resolve handle -> open viewer at the page with bbox).
        if (protocol === "cite") {
            return <InlineSourceChip handle={target} />;
        }

        // Modal links carry no affordance, and only the label's text survives:
        // raw HTML reaches both policies, so a label can itself be a control,
        // and handing back its children would leave that control focusable.
        // Parsing modal links is what keeps them out of the unknown-protocol
        // branch above, which would render a live anchor.
        if (action === "modal") {
            return <>{labelTextOf(children)}</>;
        }

        const handleClick = (e: { preventDefault: () => void }) => {
            e.preventDefault();
            if (action === "navigate") {
                onNavigate(target);
            } else if (action === "source") {
                const documentId = parseInt(target, 10);
                if (!isNaN(documentId)) {
                    const page = params?.page
                        ? parseInt(params.page, 10)
                        : undefined;
                    onSource(
                        documentId,
                        isNaN(page as number) ? undefined : page
                    );
                }
            }
        };

        // The Button paths below drop the original href; mirror it onto
        // a data attribute so the xlsx export can read it from there.
        const customLinkDataProps = { [CUSTOM_HREF_ATTR]: href };

        if (protocol === "button") {
            const icon = (() => {
                if (action === "navigate")
                    return <ExternalLink className="size-4" />;
                if (action === "source") return <FileText className="size-4" />;
                return null;
            })();

            return (
                <span className="my-1 block">
                    <Button onClick={handleClick} {...customLinkDataProps}>
                        {icon}
                        {children}
                    </Button>
                </span>
            );
        }

        // protocol === "link"
        return (
            <Button
                onClick={handleClick}
                className={cn(
                    "aui-md-a text-primary hover:text-primary/80 cursor-pointer border-none bg-transparent p-0 font-[inherit] underline underline-offset-2",
                    className
                )}
                {...customLinkDataProps}
            >
                {children}
            </Button>
        );
    },
    blockquote: ({ className, ...props }) => (
        <blockquote
            className={cn(
                "aui-md-blockquote border-muted-foreground/30 text-muted-foreground my-2.5 border-l-2 pl-3 italic",
                className
            )}
            {...props}
        />
    ),
    ul: ({ className, ...props }) => (
        <ul
            className={cn(
                "aui-md-ul marker:text-muted-foreground my-2 ml-4 list-disc [&>li]:mt-1",
                className
            )}
            {...props}
        />
    ),
    ol: ({ className, ...props }) => (
        <ol
            className={cn(
                "aui-md-ol marker:text-muted-foreground my-2 ml-4 list-decimal [&>li]:mt-1",
                className
            )}
            {...props}
        />
    ),
    hr: ({ className, ...props }) => (
        <hr
            className={cn(
                "aui-md-hr border-muted-foreground/20 my-2",
                className
            )}
            {...props}
        />
    ),
    thead: ({ className, ...props }) => (
        <thead
            className={cn(
                "aui-md-thead bg-primary text-primary-foreground [&_a]:!text-primary-foreground [&_button]:!text-primary-foreground",
                className
            )}
            {...props}
        />
    ),
    th: ({ className, ...props }) => (
        <th className={cn(TH_CLASS, className)} {...props} />
    ),
    td: ({ className, ...props }) => (
        <td className={cn(TD_CLASS, className)} {...props} />
    ),
    tr: ({ className, ...props }) => (
        <tr
            className={cn(
                "aui-md-tr [tbody_&:nth-child(odd)]:bg-muted/50",
                className
            )}
            {...props}
        />
    ),
    li: ({ className, ...props }) => (
        <li className={cn("aui-md-li leading-normal", className)} {...props} />
    ),
    sup: ({ className, ...props }) => (
        <sup
            className={cn(
                "aui-md-sup [&>a]:text-xs [&>a]:no-underline",
                className
            )}
            {...props}
        />
    ),
    pre: ({ className, ...props }) => (
        <pre
            className={cn(
                "aui-md-pre border-border/50 bg-muted/30 overflow-x-auto rounded-t-none rounded-b-lg border border-t-0 p-3 text-xs leading-relaxed",
                className
            )}
            {...props}
        />
    ),
    code: function Code({ className, ...props }) {
        const isCodeBlock = useIsMarkdownCodeBlock();
        return (
            <code
                className={cn(
                    !isCodeBlock &&
                        "aui-md-inline-code border-border/50 bg-muted/50 rounded-md border px-1.5 py-0.5 font-mono text-[0.85em]",
                    className
                )}
                {...props}
            />
        );
    },
    CodeHeader,
});

// `table` and `span` are registered after the memoize call:
// `memoizeMarkdownComponents` strips the `node` prop, and both need it —
// `MarkdownChatTable` to call `parseMarkdownTable`, `EditorialSpan` to read the
// provenance that decides whether a span is a glyph. The hand-rolled
// `memo(..., are*NodesEqual)` preserves the parent re-render fence in each case.
const defaultComponents = {
    ...memoizedDefaultComponents,
    table: MemoizedMarkdownChatTable,
    span: MemoizedEditorialSpan,
};

/**
 * What the fallback-cell walk may do with THIS app's overrides.
 *
 * Derived from the override map itself rather than restated, so it cannot name
 * a component the map no longer contains. In a react-markdown tree `a`,
 * `code`, `sup` and `span` are all components, not tags — a walk that only
 * classified tag names would meet four function components on ordinary content
 * and report every one of them, which costs the fail-closed design the only
 * feedback channel it has.
 *
 * The anchor override is transactional rather than opaque: it renders its
 * children as a label and nothing else, so typesetting inside it is safe and a
 * link whose whole label is decorative rolls back instead of emptying.
 */
const EDITORIAL_CELL_COMPONENTS: EditorialComponentPolicy = {
    opaque: new Set([
        memoizedDefaultComponents.code,
        memoizedDefaultComponents.sup,
        MemoizedEditorialSpan,
    ]),
    transactional: new Set([memoizedDefaultComponents.a]),
};

// Same cells as the plain path, with their children routed through the
// React-children adapter.
//
// Run through `memoizeMarkdownComponents` like every other override rather than
// hand-written: that helper is what strips the `node` prop react-markdown
// passes, and a cell that forwards it spreads `node="[object Object]"` onto the
// DOM. It also supplies the memoization that keeps a cell from remounting on
// every streaming frame.
const editorialCellComponents = memoizeMarkdownComponents({
    th: ({ className, children, ...props }) => (
        <th className={cn(TH_CLASS, className)} {...props}>
            <EditorialReactInline components={EDITORIAL_CELL_COMPONENTS}>
                {children}
            </EditorialReactInline>
        </th>
    ),
    td: ({ className, children, ...props }) => (
        <td className={cn(TD_CLASS, className)} {...props}>
            <EditorialReactInline components={EDITORIAL_CELL_COMPONENTS}>
                {children}
            </EditorialReactInline>
        </td>
    ),
});

/**
 * The editorial mount's component map. Only the two cells differ: everything
 * else is shared, so the fallback table is the same table with typeset cells.
 */
const editorialComponents = {
    ...defaultComponents,
    ...editorialCellComponents,
};
